import { DEFAULT_SHIFT_PRESETS } from './shift-roster';

/**
 * 근무제 표준 묶음 — 기준 화면 왼쪽 칸의 **「담을 것」** (2026-09-02 · 정본 P3-1).
 *
 * ★★ **왜 필요한가.** 종전 화면은 빈 칸 한 벌뿐이라 「우리 회사는 무엇을 만들어야 하나」를
 *    화면이 말하지 않았다. 교대에서 한 번 겪은 병이다 — 「교대근무를 선택하여도 주간·야간으로
 *    안 나온다」(2026-08-30 사용자 지적). 고르는 순간 무엇이 생기는지 보이지 않으면
 *    **아무것도 안 생긴 것과 같다.** 그 처방을 종류 다섯 전체로 넓힌 것이 이 목록이다.
 *
 * ★★ **서버에 표준 묶음 API 가 없다.** 직책은 서버가 템플릿을 준다(`/hr/job-roles/template`).
 *    근무제는 없으므로 **화면 쪽 상수**로 둔다 — 새 라우트도 마이그레이션도 만들지 않는다
 *    (main 은 언제든 그대로 빌드해 배포할 수 있는 기준선이어야 한다).
 *
 * ★ **교대 세 조는 여기서 다시 적지 않는다.** `DEFAULT_SHIFT_PRESETS` 를 그대로 쓴다 —
 *   두 곳에 적으면 언젠가 한쪽만 고쳐진다(주간 시각이 화면마다 다른 상태).
 * ★ **시각은 고칠 수 있어야 한다.** 회사마다 다르다 — 화면은 이 값을 «시작점»으로만 쓰고
 *   등록 팝업 안에서 고쳐 넣는다.
 */
export interface WorkScheduleTemplate {
  name: string;
  /** `STANDARD` · `FLEX` · `SELECT` · `ELASTIC` · `SHIFT` — `WORK_TYPE_LABEL` 의 키. */
  type: string;
  startTime: string;
  endTime: string;
  breakMinutes: number;
  workMinutes: number;
  graceMinutes: number;
  coreStart: string | null;
  coreEnd: string | null;
  flexStart: string | null;
  flexEnd: string | null;
  /** 목록에서 «어떤 자리인가» 를 한 줄로. 종류 이름만으로는 무엇이 다른지 모른다. */
  note: string;
  /** 표에서 서는 차례. 안 주면 서버가 100 을 넣어 전부 같은 자리가 된다. */
  sortOrder: number;
}

/** 시차출퇴근·선택근로의 기본 폭 — 법에 정해진 값이 아니라 **가장 흔한 값**이다. */
const FLEX_WINDOW = { start: '08:00', end: '10:00' } as const;
const CORE_WINDOW = { start: '11:00', end: '15:00' } as const;

export const WORK_SCHEDULE_TEMPLATES: readonly WorkScheduleTemplate[] = [
  {
    name: '통상근무', type: 'STANDARD',
    startTime: '09:00', endTime: '18:00', breakMinutes: 60, workMinutes: 480, graceMinutes: 0,
    coreStart: null, coreEnd: null, flexStart: null, flexEnd: null,
    note: '9-6 · 휴게 1시간', sortOrder: 10,
  },
  {
    name: '시차출퇴근', type: 'FLEX',
    startTime: '09:00', endTime: '18:00', breakMinutes: 60, workMinutes: 480, graceMinutes: 0,
    coreStart: null, coreEnd: null, flexStart: FLEX_WINDOW.start, flexEnd: FLEX_WINDOW.end,
    // ★ 선택폭이 없으면 서버가 400(FLEX_WINDOW_REQUIRED) 을 준다 — 기본값을 반드시 싣는다.
    note: `선택폭 ${FLEX_WINDOW.start}–${FLEX_WINDOW.end}`, sortOrder: 20,
  },
  {
    name: '선택근로', type: 'SELECT',
    startTime: '09:00', endTime: '18:00', breakMinutes: 60, workMinutes: 480, graceMinutes: 0,
    coreStart: CORE_WINDOW.start, coreEnd: CORE_WINDOW.end, flexStart: null, flexEnd: null,
    note: `코어 ${CORE_WINDOW.start}–${CORE_WINDOW.end}`, sortOrder: 30,
  },
  {
    name: '탄력근로', type: 'ELASTIC',
    startTime: '09:00', endTime: '18:00', breakMinutes: 60, workMinutes: 480, graceMinutes: 0,
    coreStart: null, coreEnd: null, flexStart: null, flexEnd: null,
    // ★ 아직 주 단위 배정표가 없다 — 그 사실을 목록에서 미리 말한다(만들고 나서 알면 늦다).
    note: '주 배정표는 아직 없다 · 시업 시각으로 판정', sortOrder: 40,
  },
  // ★ 교대 세 조 — 한 줄이 «조(組)» 하나다. 휴무는 소정 0분이라 그날 지각을 묻지 않는다.
  ...DEFAULT_SHIFT_PRESETS.map((p, i) => ({
    name: p.name, type: 'SHIFT',
    startTime: p.startTime, endTime: p.endTime,
    breakMinutes: p.breakMinutes, workMinutes: p.workMinutes, graceMinutes: 0,
    coreStart: null, coreEnd: null, flexStart: null, flexEnd: null,
    note: p.workMinutes === 0 ? '휴무 조 · 지각을 묻지 않는다' : '교대 조',
    sortOrder: 50 + i * 10,
  })),
];

/**
 * 이미 있는 이름은 빼고 **만들 것만** 남긴다 — 두 번 눌러도 겹치지 않는다.
 *
 * ★ **이름만으로 가른다.** 교대 전용 규칙(`presetsToCreate`)은 종류까지 봤지만, 이 목록은
 *   종류가 섞여 있어 그 규칙을 그대로 쓸 수 없다. 그리고 한 회사 안에서 **같은 이름의 근무제
 *   둘**은 배정 화면에서 어느 쪽인지 가릴 수 없으므로, 이름이 같으면 없는 셈 치지 않는다.
 */
export function templatesToCreate(
  picked: readonly WorkScheduleTemplate[],
  existing: readonly { name: string }[],
): WorkScheduleTemplate[] {
  const have = new Set(existing.map((s) => s.name.trim()));
  return picked.filter((t) => t.name.trim().length > 0 && !have.has(t.name.trim()));
}
